import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { BookOpen, Clock } from "lucide-react";

/**
 * Renders sibling volumes in a series.
 * Props: series_slug (string), current_id (string) — the current product to highlight/exclude.
 */
export default function SeriesCarousel({ series_slug, current_id }) {
  const [items, setItems] = useState([]);
  const { format } = useCurrency();

  useEffect(() => {
    if (!series_slug) return;
    api.get("/products", { params: { series: series_slug } }).then((r) => setItems(r.data)).catch(() => {});
  }, [series_slug]);

  if (!series_slug || items.length < 1) return null;

  return (
    <section className="mt-20 pt-12 border-t border-lapis/10" data-testid="series-carousel">
      <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="overline mb-3 flex items-center gap-2"><BookOpen size={12} /> The series</div>
          <h3 className="font-serif text-3xl text-lapis font-light leading-tight">
            All <em className="text-peach-deep">volumes</em> in this journey.
          </h3>
        </div>
        <span className="text-xs uppercase tracking-[0.18em] text-ink/50">
          {items.length} {items.length === 1 ? "volume" : "volumes"} so far
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((p) => {
          const isCurrent = p.id === current_id;
          return (
            <Link
              key={p.id}
              to={`/shop/${p.slug}`}
              className={`group block relative ${isCurrent ? "ring-2 ring-lapis ring-offset-2 ring-offset-canvas rounded-[1.75rem]" : ""}`}
              data-testid={`series-item-${p.slug}`}
            >
              <div className="aspect-[4/5] overflow-hidden rounded-[1.75rem] relative">
                <img src={p.image} alt={p.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/55 via-transparent to-transparent" />
                <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-canvas/85 backdrop-blur px-3 py-1 rounded-full overline">
                  <Clock size={10} /> Volume {p.volume_number || "—"}
                </div>
                {isCurrent && (
                  <div className="absolute top-3 right-3 bg-lapis text-canvas text-[10px] uppercase tracking-[0.18em] px-3 py-1 rounded-full font-semibold">
                    You're here
                  </div>
                )}
              </div>
              <h4 className="mt-4 font-serif text-lg text-ink leading-tight group-hover:text-lapis transition-colors">{p.title}</h4>
              <div className="mt-2 flex items-center justify-between">
                <span className="font-serif text-lg text-lapis">{format(p.price, p.price_inr)}</span>
                {!isCurrent && <span className="text-xs uppercase tracking-widest text-ink/55 group-hover:text-lapis">Read →</span>}
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
